import { useCallback, useEffect, useRef } from 'react';
import { trackTikTokPageView } from '@/integrations/monitoring/tiktok';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getAttributionPayload } from '@/lib/attribution';

// Generate a session ID that persists across page navigations but not browser sessions
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('amorette_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('amorette_session_id', sessionId);
  }
  return sessionId;
};

type EventCategory = 'page_view' | 'funnel' | 'engagement' | 'conversion';

interface TrackEventOptions {
  event_name: string;
  event_category: EventCategory;
  event_data?: Record<string, unknown> | undefined;
  page_path?: string | undefined;
}

/**
 * Non-identifying device classification. Never include user agent details,
 * credentials, payment data or private content in analytics payloads.
 */
const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const ua = navigator.userAgent;
  if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua)) return 'tablet';
  if (/Mobi|iPhone|Android/i.test(ua)) return 'mobile';
  return 'desktop';
};

/** Referral host only — never the full referring URL with its query string. */
const getReferrerDomain = (): string | null => {
  try {
    return document.referrer ? new URL(document.referrer).hostname : null;
  } catch {
    return null;
  }
};

export const useAnalytics = () => {
  const { user } = useAuth();
  const trackedPages = useRef<Set<string>>(new Set());

  const track = useCallback(async (options: TrackEventOptions) => {
    try {
      const attribution = getAttributionPayload();
      await supabase.from('analytics_events').insert([{
        user_id: user?.id ?? null,
        session_id: getSessionId(),
        event_name: options.event_name,
        event_category: options.event_category,
        event_data: {
          ...(options.event_data ?? {}),
          device_type: getDeviceType(),
          referrer_domain: getReferrerDomain(),
        } as unknown as Record<string, never>,
        page_path: options.page_path ?? window.location.pathname,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        utm_source: attribution.utm_source,
        utm_medium: attribution.utm_medium,
        utm_campaign: attribution.utm_campaign,
        utm_content: attribution.utm_content,
        utm_term: attribution.utm_term,
        landing_page: attribution.landing_page,
      }]);
    } catch (err) {
      // Analytics should never break the app
      console.warn('Analytics track failed:', err);
    }
  }, [user?.id]);

  const trackPageView = useCallback((pageName: string) => {
    const path = window.location.pathname;
    const key = `${pageName}:${path}`;
    if (trackedPages.current.has(key)) return;
    trackedPages.current.add(key);

    track({
      event_name: 'page_view',
      event_category: 'page_view',
      event_data: { page_name: pageName },
      page_path: path,
    });

    // Fire TikTok PageView on every tracked page
    trackTikTokPageView();
  }, [track]);

  const trackFunnel = useCallback((step: string, data?: Record<string, unknown>) => {
    track({
      event_name: step,
      event_category: 'funnel',
      event_data: data,
    });
  }, [track]);

  const trackConversion = useCallback((action: string, data?: Record<string, unknown>) => {
    track({
      event_name: action,
      event_category: 'conversion',
      event_data: data,
    });
  }, [track]);

  return { track, trackPageView, trackFunnel, trackConversion };
};
